import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { getTranslation } from '../../../utils/translations';
import { isValidRoleTypeSlug, normalizeRoleTypeSlug } from '../../../lib/userRoleTypes';
import { userRoleService } from '../../../services/userRoleService';
import { toast } from 'sonner';

const emptyForm = {
  roleType: '',
  roleName: '',
  description: '',
  canBeClient: false,
  canBeSupplier: false,
  isActive: true,
};

const RoleFormModal = ({
  isOpen,
  onClose,
  mode = 'create',
  role,
  existingRoles,
  language = 'FR',
  onSaved,
}) => {
  const tr = getTranslation(language);
  const rc = tr?.adminRoleCrud || {};
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && role) {
      setFormData({
        roleType: role?.roleType || '',
        roleName: role?.roleName || '',
        description: role?.description || '',
        canBeClient: !!role?.canBeClient,
        canBeSupplier: !!role?.canBeSupplier,
        isActive: role?.isActive !== false,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
    setSaving(false);
  }, [isOpen, mode, role]);

  const usedTypes = new Set(
    (existingRoles || [])
      .map((r) => normalizeRoleTypeSlug(r?.roleType))
      .filter(Boolean)
  );

  const validateCreate = () => {
    const e = {};
    const slug = normalizeRoleTypeSlug(formData.roleType);
    if (!slug) e.roleType = rc.errors?.roleTypeRequired;
    else if (!isValidRoleTypeSlug(slug)) e.roleType = rc.errors?.roleTypeInvalidSnake;
    if (!formData.roleName?.trim()) e.roleName = rc.errors?.roleNameRequired;
    if (slug && usedTypes.has(slug)) e.roleType = rc.errors?.roleTypeTaken;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateEdit = () => {
    const e = {};
    if (!formData.roleName?.trim()) e.roleName = rc.errors?.roleNameRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const ok = mode === 'create' ? validateCreate() : validateEdit();
    if (!ok) return;

    setSaving(true);
    try {
      if (mode === 'create') {
        await userRoleService.createRole({
          roleType: normalizeRoleTypeSlug(formData.roleType),
          roleName: formData.roleName.trim(),
          description: formData.description?.trim() || null,
          canBeClient: formData.canBeClient,
          canBeSupplier: formData.canBeSupplier,
          isActive: formData.isActive,
        });
        toast.success(language === 'FR' ? 'Rôle créé' : 'Role created');
      } else if (role?.id) {
        await userRoleService.updateRole(role.id, {
          roleName: formData.roleName.trim(),
          description: formData.description?.trim() ?? null,
          canBeClient: formData.canBeClient,
          canBeSupplier: formData.canBeSupplier,
          isActive: formData.isActive,
        });
        toast.success(language === 'FR' ? 'Rôle mis à jour' : 'Role updated');
      }

      await onSaved?.();
      onClose();
    } catch (err) {
      console.error('Role save error:', err);
      toast.error(language === 'FR' ? 'Échec' : 'Failed', {
        description: err?.message || String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{mode === 'create' ? rc.newRole : rc.editRole}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-muted">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {mode === 'create' && rc.createExplain && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              {rc.createExplain}
            </div>
          )}
          {mode === 'create' ? (
            <>
              <Input
                label={rc.roleType}
                placeholder={rc.roleTypePlaceholder}
                value={formData.roleType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, roleType: e.target.value }))
                }
                onBlur={() =>
                  setFormData((prev) => ({
                    ...prev,
                    roleType: normalizeRoleTypeSlug(prev.roleType),
                  }))
                }
                error={errors.roleType}
                description={rc.roleTypeHint}
                required
                autoComplete="off"
                spellCheck={false}
              />
              <Input
                label={rc.roleName}
                value={formData.roleName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, roleName: e.target.value }))
                }
                error={errors.roleName}
                required
              />
            </>
          ) : (
            <>
              <div>
                <p className="text-sm font-medium text-foreground">{rc.roleType}</p>
                <p className="text-sm font-mono text-muted-foreground mt-1">{formData.roleType}</p>
                <p className="text-xs text-muted-foreground mt-1">{rc.roleTypeReadOnly}</p>
              </div>
              <Input
                label={rc.roleName}
                value={formData.roleName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, roleName: e.target.value }))
                }
                error={errors.roleName}
                required
              />
            </>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{rc.description}</label>
            <textarea
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>

          <div className="space-y-3">
            <Checkbox
              label={rc.canBeClient}
              checked={formData.canBeClient}
              onCheckedChange={(c) =>
                setFormData((prev) => ({ ...prev, canBeClient: !!c }))
              }
            />
            <Checkbox
              label={rc.canBeSupplier}
              checked={formData.canBeSupplier}
              onCheckedChange={(c) =>
                setFormData((prev) => ({ ...prev, canBeSupplier: !!c }))
              }
            />
            <Checkbox
              label={rc.isActive}
              checked={formData.isActive}
              onCheckedChange={(c) =>
                setFormData((prev) => ({ ...prev, isActive: !!c }))
              }
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {rc.cancel || tr?.common?.cancel}
            </Button>
            <Button type="submit" variant="default" loading={saving}>
              {rc.save || tr?.common?.save}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleFormModal;
